// ============================================================
// FILE: PostJob.js
// LOCATION: C:\gardenmarket\web\src\pages\buyer\PostJob.js
// ============================================================
// PURPOSE:
//   Buyer's job posting form. Two-step flow:
//   Step 1: job details (with SuburbSearch instead of full dropdown)
//   Step 2: optional photo upload
// ============================================================

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import PhotoUploader from '../../components/PhotoUploader';
import SuburbSearch from '../../components/SuburbSearch';

export default function PostJob() {
    const navigate    = useNavigate();
    const { user }    = useAuth();
    const [skills,    setSkills]    = useState([]);
    const [jobTypes,  setJobTypes]  = useState([]);
    const [error,     setError]     = useState('');
    const [loading,   setLoading]   = useState(false);
    const [createdJobId, setCreatedJobId] = useState(null);
    const [jobPhotos, setJobPhotos] = useState([]);

    // ── Referral / direct-booking target ────────────────────
    // Set when the buyer arrived via a gardener's link (/g/CODE)
    // or picked a gardener from Browse. Determines whether the
    // job is exclusive, and for how long:
    //   referral -> permanent (the gardener brought this customer)
    //   browse   -> 24 hours  (the buyer was shopping)
    const location = useLocation();
    const [targetGardener, setTargetGardener] = useState(
        location.state?.gardener || null
    );
    const [jobSource, setJobSource] = useState(
        location.state?.referralCode ? 'referral'
      : location.state?.gardener     ? 'browse'
      : 'marketplace'
    );

    // Resolve a referral code stashed before register/login.
    useEffect(() => {
        if (targetGardener) return;
        let code = null;
        try { code = sessionStorage.getItem('gh_referral_code'); } catch { /* blocked */ }
        if (!code) return;

        let cancelled = false;
        client.get(`/gardeners/by-code/${encodeURIComponent(code)}`)
            .then(r => {
                if (cancelled) return;
                setTargetGardener(r.data.gardener);
                setJobSource('referral');
            })
            .catch(() => { /* stale or invalid code — post normally */ });
        return () => { cancelled = true; };
    }, [targetGardener]);

    const clearTarget = () => {
        setTargetGardener(null);
        setJobSource('marketplace');
        try { sessionStorage.removeItem('gh_referral_code'); } catch { /* ignore */ }
    };

    // Suburb search state
    const [suburb,  setSuburb]  = useState(null);
    const [country, setCountry] = useState('AU');

    const [form, setForm] = useState({
        title:          '',
        description:    '',
        job_type_code:  'one_off',
        preferred_date: '',
        budget:         '',
        skill_ids:      [],
    });

    useEffect(() => {
        Promise.all([
            client.get('/ref/skills'),
            client.get('/ref/job-types'),
        ]).then(([sk, jt]) => {
            setSkills(sk.data.skills);
            setJobTypes(jt.data.job_types);
        });
        // Default country to buyer's own if available
        if (user?.country) setCountry(user.country);
    }, [user]);

    const toggleSkill = (id) => {
        setForm(f => ({
            ...f,
            skill_ids: f.skill_ids.includes(id)
                ? f.skill_ids.filter(s => s !== id)
                : [...f.skill_ids, id]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!suburb) { setError('Please select a suburb for this job.'); return; }
        setError('');
        setLoading(true);
        try {
            const res = await client.post('/jobs', {
                ...form,
                suburb_id: suburb.suburb_id,
                budget:    form.budget ? parseFloat(form.budget) : null,
                // Only sent when the buyer arrived via a gardener
                // link or picked one from Browse.
                target_gardener_id: targetGardener?.gardener_id || null,
                job_source:         targetGardener ? jobSource : 'marketplace',
            });
            setCreatedJobId(res.data.job.job_id);
            // Consume the code so a later, unrelated job is not
            // silently attributed to the same gardener.
            try { sessionStorage.removeItem('gh_referral_code'); } catch { /* ignore */ }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to post job.');
        } finally {
            setLoading(false);
        }
    };

    const grouped = skills.reduce((acc, s) => {
        if (!acc[s.category]) acc[s.category] = [];
        acc[s.category].push(s);
        return acc;
    }, {});

    // ── Step 2: photo upload ──────────────────────────────
    if (createdJobId) {
        return (
            <div style={styles.page}>
                <h1 style={styles.title}>Add Photos (optional)</h1>
                <p style={styles.sub}>
                    Photos help gardeners understand the job and quote accurately.
                    You can skip this and add photos later from the job detail page.
                </p>
                <div style={styles.form}>
                    <PhotoUploader
                        uploadUrl={`/photos/job/${createdJobId}`}
                        extraFields={{ photo_type: 'job_posting' }}
                        existingPhotos={jobPhotos}
                        onDeleteUrl={(photo) => `/photos/job/${photo.photo_id}`}
                        onChange={async () => {
                            const r = await client.get(`/jobs/${createdJobId}`);
                            setJobPhotos(r.data.job.photos || []);
                        }}
                        label="Job photos"
                        maxFiles={9}
                    />
                    <button onClick={() => navigate(`/buyer/job/${createdJobId}`)} style={styles.btn}>
                        {jobPhotos.length > 0 ? 'Done' : 'Skip & View Job'}
                    </button>
                </div>
            </div>
        );
    }

    // ── Step 1: job details form ──────────────────────────
    return (
        <div style={styles.page}>
            <h1 style={styles.title}>Post a Job</h1>
            <p style={styles.sub}>Describe your gardening job and we'll match you with local gardeners.</p>

            {error && <div style={styles.error}>{error}</div>}

            {targetGardener && (
                <div style={{
                    background:'#e8f5e9', border:'1px solid #a5d6a7',
                    borderRadius:'8px', padding:'14px 16px', marginBottom:'16px',
                }}>
                    <div style={{ fontWeight:600, color:'#14532D', marginBottom:'4px' }}>
                        Sending this job to {targetGardener.first_name}
                    </div>
                    <div style={{ fontSize:'0.85rem', color:'#14532D', lineHeight:1.5 }}>
                        {jobSource === 'referral'
                            ? 'Only they will see it — other gardeners won\u2019t be notified.'
                            : 'They get first look for 24 hours. After that other local gardeners can quote too.'}
                    </div>
                    <button
                        type="button"
                        onClick={clearTarget}
                        style={{
                            background:'none', border:'none', padding:'6px 0 0',
                            color:'#2d6a4f', fontSize:'0.8rem', cursor:'pointer',
                            textDecoration:'underline',
                        }}
                    >
                        Post to all local gardeners instead
                    </button>
                </div>
            )}

            <form onSubmit={handleSubmit} style={styles.form}>

                <div style={styles.field}>
                    <label style={styles.label}>Job title *</label>
                    <input
                        value={form.title}
                        onChange={e => setForm({...form, title: e.target.value})}
                        style={styles.input}
                        placeholder="e.g. Lawn mow and edge — large backyard"
                        required
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Description *</label>
                    <textarea
                        value={form.description}
                        onChange={e => setForm({...form, description: e.target.value})}
                        style={{...styles.input, height:'120px', resize:'vertical'}}
                        placeholder="Describe the job in detail — size of yard, access, anything special..."
                        required
                    />
                </div>

                {/* Suburb search — replaces the old full dropdown */}
                <div style={styles.field}>
                    <SuburbSearch
                        label="Job location *"
                        value={suburb}
                        onChange={setSuburb}
                        country={country}
                        onCountryChange={setCountry}
                        placeholder="Type suburb name..."
                        showCountry={true}
                        required={true}
                    />
                </div>

                <div className="gh-grid-2">
                    <div style={styles.field}>
                        <label style={styles.label}>Job type</label>
                        <select
                            value={form.job_type_code}
                            onChange={e => setForm({...form, job_type_code: e.target.value})}
                            style={styles.input}
                        >
                            {jobTypes.map(t => (
                                <option key={t.job_type_code} value={t.job_type_code}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Preferred date</label>
                        <input
                            type="date"
                            value={form.preferred_date}
                            onChange={e => setForm({...form, preferred_date: e.target.value})}
                            style={styles.input}
                        />
                    </div>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Budget (optional)</label>
                    <input
                        type="number"
                        value={form.budget}
                        onChange={e => setForm({...form, budget: e.target.value})}
                        style={styles.input}
                        placeholder="Leave blank to receive all quotes"
                        min="0"
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Skills required</label>
                    <p style={styles.hint}>Select all that apply — helps us match the right gardeners</p>
                    {Object.entries(grouped).map(([category, catSkills]) => (
                        <div key={category} style={styles.skillGroup}>
                            <div style={styles.skillCat}>{category}</div>
                            <div style={styles.skillChips}>
                                {catSkills.map(s => (
                                    <button
                                        key={s.skill_id}
                                        type="button"
                                        onClick={() => toggleSkill(s.skill_id)}
                                        style={form.skill_ids.includes(s.skill_id)
                                            ? styles.chipActive : styles.chip}
                                    >
                                        {s.skill_name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <button type="submit" style={styles.btn} disabled={loading}>
                    {loading ? 'Posting...' : 'Continue — Add Photos Next'}
                </button>
            </form>
        </div>
    );
}

const styles = {
    page:       { maxWidth:'700px', margin:'0 auto', padding:'32px 24px' },
    title:      { color:'#2d6a4f', marginBottom:'8px' },
    sub:        { color:'#666', marginBottom:'32px' },
    form:       { background:'white', borderRadius:'12px', padding:'32px', boxShadow:'0 2px 12px rgba(0,0,0,0.08)' },
    error:      { backgroundColor:'#fee', border:'1px solid #fcc', borderRadius:'6px', padding:'10px 14px', color:'#c00', marginBottom:'16px' },
    field:      { marginBottom:'20px' },
    // row columns live in responsive.css (.gh-grid-2) so the form
    // collapses to a single column on phones.
    label:      { display:'block', fontWeight:'600', marginBottom:'6px', color:'#333', fontSize:'0.9rem' },
    hint:       { color:'#888', fontSize:'0.82rem', margin:'-2px 0 8px' },
    input:      { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'1rem', boxSizing:'border-box' },
    skillGroup: { marginBottom:'12px' },
    skillCat:   { fontSize:'0.8rem', fontWeight:'bold', color:'#888', textTransform:'uppercase', marginBottom:'6px' },
    skillChips: { display:'flex', flexWrap:'wrap', gap:'8px' },
    chip:       { padding:'5px 12px', borderRadius:'16px', border:'1px solid #ddd', background:'white', cursor:'pointer', fontSize:'0.85rem' },
    chipActive: { padding:'5px 12px', borderRadius:'16px', border:'1px solid #2d6a4f', background:'#e8f5e9', color:'#2d6a4f', cursor:'pointer', fontSize:'0.85rem', fontWeight:'bold' },
    btn:        { width:'100%', padding:'14px', backgroundColor:'#2d6a4f', color:'white', border:'none', borderRadius:'8px', fontSize:'1rem', fontWeight:'bold', cursor:'pointer', marginTop:'16px' },
};
